import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { postJsonResilient } from "../lib/httpClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UserContext = createContext({ user: null, loading: true, login: () => {}, register: () => {}, logout: () => {}, refreshUser: () => {} });

export const useUser = () => useContext(UserContext);

// Keep axios' default Authorization header in sync with the stored session
// token so every API call (e.g. the now-authenticated /store endpoints) can
// identify the logged-in user and operate on their own wallet only.
const syncAuthHeader = () => {
  const token = localStorage.getItem("user_session_token");
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};
syncAuthHeader();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem("user_session_token");
      const res = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  /**
   * Resilient POST helper: tries fetch first, falls back to XMLHttpRequest if
   * a browser extension or platform interceptor breaks fetch via postMessage cloning.
   */
  const postJson = postJsonResilient;

  const login = async (email, password) => {
    const { ok, data } = await postJson(`${API}/auth/login`, { email, password });
    if (!ok) throw new Error(data.detail || "Login failed");
    localStorage.setItem("user_session_token", data.token);
    syncAuthHeader();
    setUser(data.user);
    window.dispatchEvent(new Event("user-auth-changed"));
    return data;
  };

  const register = async (email, password, name) => {
    const { ok, data } = await postJson(`${API}/auth/register`, { email, password, name });
    if (!ok) throw new Error(data.detail || "Registration failed");
    localStorage.setItem("user_session_token", data.token);
    syncAuthHeader();
    setUser(data.user);
    window.dispatchEvent(new Event("user-auth-changed"));
    return data;
  };

  const googleLogin = async (sessionId) => {
    const res = await fetch(`${API}/auth/google-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId })
    });
    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error("Server error. Please try again."); }
    if (!res.ok) throw new Error(data.detail || "Google login failed");
    localStorage.setItem("user_session_token", data.token);
    syncAuthHeader();
    setUser(data.user);
    window.dispatchEvent(new Event("user-auth-changed"));
    return data;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("user_session_token");
      await fetch(`${API}/auth/user-logout`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch {}
    localStorage.removeItem("user_session_token");
    syncAuthHeader();
    setUser(null);
    window.dispatchEvent(new Event("user-auth-changed"));
  };

  const refreshUser = () => checkAuth();

  return (
    <UserContext.Provider value={{ user, loading, login, register, googleLogin, logout, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
}
